import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { api, unwrap } from '../api/client.js';
import { PageTitle } from '../components/Layouts.js';
import { Card, LoadError, Loading, Notice, SelectField } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDateTime, formatDuration } from '../i18n.js';
import { useMe } from '../session.js';

type Filter = 'all' | 'open' | 'resolved';

/** S5: outages with start, duration and cause. */
export function Incidents() {
  const { siteId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const timeZone = useMe().data?.account.timezone;
  const [filter, setFilter] = useState<Filter>('all');
  const incidents = useQuery({
    queryKey: ['site', siteId, 'incidents', filter],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/incidents', {
          params: { path: { siteId }, query: { status: filter, limit: 100 } },
        }),
      ),
  });
  return (
    <>
      <PageTitle>{t('incidents.title')}</PageTitle>
      <div className="mb-4 max-w-xs">
        <SelectField
          label={t('incidents.filter')}
          value={filter}
          onChange={(e) => setFilter(e.target.value as Filter)}
        >
          {(['all', 'open', 'resolved'] as const).map((f) => (
            <option key={f} value={f}>
              {t(`incidents.filters.${f}`)}
            </option>
          ))}
        </SelectField>
      </div>
      {incidents.isPending ? <Loading /> : null}
      {incidents.isError ? (
        <LoadError
          message={errorMessage(incidents.error, t)}
          onRetry={() => void incidents.refetch()}
        />
      ) : null}
      {incidents.data && incidents.data.items.length === 0 ? (
        <Notice kind="success">{t('incidents.empty')}</Notice>
      ) : null}
      {incidents.data && incidents.data.items.length > 0 ? (
        <Card className="overflow-x-auto">
          <table className="w-full min-w-[36rem] text-left">
            <caption className="sr-only">{t('incidents.title')}</caption>
            <thead>
              <tr className="border-b border-slate-300">
                <th scope="col" className="py-2 pr-4">
                  {t('incidents.start')}
                </th>
                <th scope="col" className="py-2 pr-4">
                  {t('incidents.cause')}
                </th>
                <th scope="col" className="py-2 pr-4">
                  {t('incidents.duration')}
                </th>
                <th scope="col" className="py-2">
                  {t('incidents.state')}
                </th>
              </tr>
            </thead>
            <tbody>
              {incidents.data.items.map((i) => (
                <tr key={i.id} className="border-b border-slate-100 align-top">
                  <td className="py-2 pr-4">
                    {formatDateTime(i.startedAt, i18n.language, timeZone)}
                  </td>
                  <td className="py-2 pr-4">
                    <span className="font-medium">
                      {t(`incidents.kinds.${i.kind}`, { defaultValue: i.kind })}
                    </span>
                    <br />
                    <span className="text-sm text-slate-700">
                      {t(`incidents.causes.${i.causeCode}`, {
                        defaultValue: t('incidents.causes.fallback'),
                      })}
                    </span>
                    {i.flapping ? (
                      <span className="ml-1 text-sm text-slate-700">
                        ({t('incidents.unstable')})
                      </span>
                    ) : null}
                  </td>
                  <td className="py-2 pr-4">
                    {i.durationSeconds === null
                      ? '–'
                      : formatDuration(i.durationSeconds, i18n.language)}
                  </td>
                  <td className="py-2">
                    {i.resolvedAt ? (
                      t('incidents.resolved')
                    ) : (
                      <strong className="text-red-800">{t('incidents.ongoing')}</strong>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-sm text-slate-700">{t('incidents.confidence')}</p>
        </Card>
      ) : null}
    </>
  );
}
